import React, { useState } from 'react';

const MATERIAL_TAGS = ['Leaflets & flyers', 'Social media', 'Talking points', 'Media guides', 'Training materials', 'Lobby & advocacy', 'Student activism', 'Tech & apps', 'Government programmes', 'Video'];
const TACTIC_TAGS = ['Manufactured consent', 'Astroturfing', 'Dehumanisation', 'False equivalence', 'Historical erasure', 'Historical revisionism', 'Normalisation', 'Deflection', 'Emotional manipulation', 'Appeal to security', 'Victim framing', 'Censorship & suppression', 'Disinformation', 'Colonisation marketing'];
const MATERIAL_TYPES = ['pdf', 'video', 'website', 'document', 'image', 'report'];

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function MaterialForm({ material = null, orgs = [], onSave, onClose }) {
  const isEdit = !!material;
  const [form, setForm] = useState({
    title: material?.title || '',
    description: material?.description || '',
    date: material?.date || '',
    type: material?.type || 'pdf',
    orgId: material?.orgId || '',
    hostedUrl: material?.hostedUrl || '',
    externalUrl: material?.externalUrl || '',
    pages: material?.pages || '',
    tags: material?.tags || [],
    tactics: material?.tactics || [],
    sources: material?.sources || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const toggleTag = (tag, field) => {
    set(field, form[field].includes(tag) ? form[field].filter(t => t !== tag) : [...form[field], tag]);
  };

  const addSource = () => set('sources', [...form.sources, { name: '', url: '' }]);
  const updateSource = (idx, field, value) => {
    const updated = [...form.sources];
    updated[idx] = { ...updated[idx], [field]: value };
    set('sources', updated);
  };
  const removeSource = (idx) => set('sources', form.sources.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.orgId) { setError('Organisation is required'); return; }

    setSaving(true);
    setError('');
    try {
      const data = {
        ...form,
        slug: material?.slug || slugify(form.title),
        id: material?.id || slugify(form.title),
        pages: form.pages ? parseInt(form.pages, 10) : null,
        sources: form.sources.filter(s => s.name.trim() || s.url.trim()),
      };
      await onSave(data);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-backdrop" onClick={onClose}>
      <div className="admin-modal admin-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="admin-modal-header">
          <h3>{isEdit ? 'Edit Material' : 'New Material'}</h3>
          <button className="admin-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal-body">
            {error && <div className="admin-error">{error}</div>}

            <label className="admin-label">Title *</label>
            <input className="admin-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="Material title" />

            <label className="admin-label">Description</label>
            <textarea className="admin-input" value={form.description} onChange={e => set('description', e.target.value)} rows={3} placeholder="Brief description..." />

            <div className="admin-row">
              <div className="admin-field">
                <label className="admin-label">Date</label>
                <input type="date" className="admin-input" value={form.date} onChange={e => set('date', e.target.value)} />
              </div>
              <div className="admin-field">
                <label className="admin-label">Type</label>
                <select className="admin-input" value={form.type} onChange={e => set('type', e.target.value)}>
                  {MATERIAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="admin-field">
                <label className="admin-label">Pages</label>
                <input type="number" className="admin-input" value={form.pages} onChange={e => set('pages', e.target.value)} placeholder="e.g. 24" />
              </div>
            </div>

            <label className="admin-label">Organisation *</label>
            <select className="admin-input" value={form.orgId} onChange={e => set('orgId', e.target.value)}>
              <option value="">Select organisation...</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>

            <label className="admin-label">Hosted URL (PDF on this site)</label>
            <input className="admin-input" value={form.hostedUrl} onChange={e => set('hostedUrl', e.target.value)} placeholder="/materials/filename.pdf" />

            <label className="admin-label">External URL</label>
            <input className="admin-input" value={form.externalUrl} onChange={e => set('externalUrl', e.target.value)} placeholder="https://..." />

            <label className="admin-label">Content tags</label>
            <div className="admin-tags-grid">
              {MATERIAL_TAGS.map(tag => (
                <button key={tag} type="button" className={`admin-tag-option ${form.tags.includes(tag) ? 'active' : ''}`} onClick={() => toggleTag(tag, 'tags')}>
                  {tag}
                </button>
              ))}
            </div>

            <label className="admin-label">Tactics</label>
            <div className="admin-tags-grid">
              {TACTIC_TAGS.map(tag => (
                <button key={tag} type="button" className={`admin-tag-option ${form.tactics.includes(tag) ? 'active' : ''}`} onClick={() => toggleTag(tag, 'tactics')}>
                  {tag}
                </button>
              ))}
            </div>

            <label className="admin-label">Sources</label>
            {form.sources.map((s, idx) => (
              <div key={idx} className="admin-source-row">
                <input className="admin-input" value={s.name} onChange={e => updateSource(idx, 'name', e.target.value)} placeholder="Source name" />
                <input className="admin-input" value={s.url} onChange={e => updateSource(idx, 'url', e.target.value)} placeholder="https://..." />
                <button type="button" className="admin-btn-remove" onClick={() => removeSource(idx)}>×</button>
              </div>
            ))}
            <button type="button" className="admin-btn-add" onClick={addSource}>+ Add source</button>
          </div>
          <div className="admin-modal-footer">
            <button type="button" className="admin-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="admin-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
